import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  PgHero, PgHeroInner, PgTitle, PgIn, PgPill, PgSurface,
  PgSplit, PgSplitText, PgSplitPanel, PgFeats, PgMini,
  PgOutro, PgOutroItem,
} from './product';

const APP_URL = 'https://borderou.polytooldesign.com';

const surfaceStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 1.05 } },
};
const surfaceItem = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 140, damping: 16 } },
};

export const BorderouPage = () => {
  return (
    <>
      {/* Hero */}
      <PgHero>
        <PgHeroInner>
          <PgTitle
            lines={[
              'Niciun termen ratat,',
              <span className="accent">niciun registru scris de mână.</span>,
            ]}
          />
          <PgIn delay={0.65}>
            <p className="pg-lead">
              Borderou urmărește dosarele tale pe portal.just.ro și îți aduce fiecare termen nou în calendar.
              Registrul de ieșiri se completează singur, din documentele trimise.
            </p>
          </PgIn>
          <PgIn delay={0.78} className="pg-cta">
            <PgPill href={APP_URL} target="_blank" rel="noopener noreferrer">
              Deschide Borderou <ArrowRight size={17} />
            </PgPill>
            <a href="#detalii" className="pg-ghost">Vezi cum arată</a>
          </PgIn>
        </PgHeroInner>

        <PgSurface id="detalii">
          <div className="pg-surface-head">
            <span className="pg-surface-title">Dosar 6789/117/2025</span>
            <span className="pg-surface-meta">Vantera Prim SRL · portal.just.ro</span>
          </div>
          <motion.div
            className="pg-surface-body"
            initial="hidden"
            animate="visible"
            variants={surfaceStagger}
          >
            <motion.div className="pg-fieldset" variants={surfaceItem}>
              <div className="pg-field"><span className="k">Parte urmărită</span><span className="v">Vantera Prim S.R.L.</span></div>
              <div className="pg-field"><span className="k">Instanță</span><span className="v">Tribunalul Constanța</span></div>
              <div className="pg-field"><span className="k">Verificat</span><span className="v">automat, azi</span></div>
            </motion.div>

            <motion.div
              variants={{
                hidden: { opacity: 0, x: -10 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
              }}
              style={{ display: 'flex' }}
            >
              <ArrowRight className="pg-arrow" size={22} />
            </motion.div>

            <motion.div
              className="pg-doclist"
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
            >
              {[
                { label: 'Termen în calendar', n: '14.05' },
                { label: 'Număr de ieșire', n: '041' },
                { label: 'Notificare trimisă', n: null },
              ].map((d) => (
                <motion.div key={d.label} className="pg-doc" variants={surfaceItem}>
                  <Check className="tick" size={15} />
                  {d.label}
                  {d.n && <span className="n">{d.n}</span>}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </PgSurface>
      </PgHero>

      {/* Split 1 — monitoring */}
      <section className="section">
        <div className="container">
          <PgSplit>
            <PgSplitText>
              <h2 className="pg-split-h">Vezi fiecare termen nou <span className="accent">fără să-l cauți.</span></h2>
              <p className="pg-split-p">
                Adaugi părțile pe care le urmărești, iar Borderou verifică portalul instanțelor automat —
                noaptea și în timpul zilei. Când apare un termen sau o schimbare, o ai imediat în agendă.
                Dosarele noi apărute pe portal sunt preluate singure, fără să le mai cauți.
              </p>
              <PgFeats items={[
                'Sincronizare cu portal.just.ro',
                'Termene și ședințe direct în calendar',
                'Dosare noi preluate automat de pe portal',
              ]} />
            </PgSplitText>
            <PgSplitPanel>
              <PgMini
                label="Actualizare dosar"
                rows={[
                  { k: 'Dosar', v: '6789/117/2025' },
                  { k: 'Termen nou', v: '14.05.2026', hi: true },
                  { k: 'Sala · ora', v: '3 · 09:00' },
                ]}
              />
            </PgSplitPanel>
          </PgSplit>
        </div>
      </section>

      {/* Split 2 — registru + team */}
      <section className="section">
        <div className="container">
          <PgSplit reverse>
            <PgSplitPanel>
              <PgMini
                label="Registru de ieșiri"
                rows={[
                  { k: '041 · Notificare creditor', v: '02.04' },
                  { k: '040 · Confirmare primire', v: '02.04' },
                  { k: '039 · Adresă instanță', v: '01.04' },
                ]}
              />
            </PgSplitPanel>
            <PgSplitText>
              <h2 className="pg-split-h">Registrul se scrie singur, <span className="accent">legat de Plicuri în ambele sensuri.</span></h2>
              <p className="pg-split-p">
                Fiecare document trimis din Plicuri apare cu număr de ieșire atribuit — fără salturi și fără
                introducere manuală. Termenele dintr-o sentință procesată în Plicuri intră singure în
                calendarul de aici, iar sentințele confirmate în Borderou devin dosare gata de corespondență
                în Plicuri. Documentele sunt citite de un model AI procesat în UE, totul sincronizat în timp
                real între membrii organizației.
              </p>
              <PgFeats items={[
                'Numerotare automată, conectată cu Plicuri',
                'Termene și dosare sincronizate în ambele sensuri',
                'Roluri și sincronizare în timp real, analiză AI în UE',
              ]} />
            </PgSplitText>
          </PgSplit>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <PgOutro>
            <PgOutroItem as="h2" className="pg-h1" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)', marginBottom: '1rem' }}>
              Borderou este <span className="accent">disponibil acum.</span>
            </PgOutroItem>
            <PgOutroItem as="p" className="pg-lead" style={{ margin: '0 auto 2.25rem' }}>
              Intră în aplicație și adaugă primele dosare pe care le urmărești.
            </PgOutroItem>
            <PgOutroItem className="pg-cta" style={{ justifyContent: 'center' }}>
              <PgPill href={APP_URL} target="_blank" rel="noopener noreferrer">
                Deschide aplicația <ArrowRight size={17} />
              </PgPill>
            </PgOutroItem>
          </PgOutro>
        </div>
      </section>
    </>
  );
};

export default BorderouPage;
